import type { ReactElement, RefObject } from 'react'
import { Search, X } from 'lucide-react'
import type { MediaCounts, MediaFilter, MediaSort } from '@shared/types'
import { MEDIA_FILTERS, MEDIA_FILTER_LABELS, MEDIA_SORTS } from '@shared/types'
import type { SelectOption } from '@/components/ui'
import { IconButton, Input, SegmentedControl, Select } from '@/components/ui'

export interface MediaToolbarProps {
  filter: MediaFilter
  sort: MediaSort
  query: string
  /** Null until the first page lands, which hides the pill counts rather than showing zeros. */
  counts: MediaCounts | null
  onFilterChange: (filter: MediaFilter) => void
  onSortChange: (sort: MediaSort) => void
  onQueryChange: (query: string) => void
  /** Target for the `/` shortcut, registered by the page. */
  searchRef?: RefObject<HTMLInputElement | null>
}

const SORT_LABELS: Record<MediaSort, string> = {
  newest: 'Newest first',
  oldest: 'Oldest first',
  title: 'Title A–Z',
  updated: 'Recently updated'
}

const SORT_OPTIONS: SelectOption[] = MEDIA_SORTS.map((sort) => ({
  value: sort,
  label: SORT_LABELS[sort]
}))

function toFilter(value: string): MediaFilter | null {
  return (MEDIA_FILTERS as readonly string[]).includes(value) ? (value as MediaFilter) : null
}

function toSort(value: string): MediaSort | null {
  return (MEDIA_SORTS as readonly string[]).includes(value) ? (value as MediaSort) : null
}

export function MediaToolbar({
  filter,
  sort,
  query,
  counts,
  onFilterChange,
  onSortChange,
  onQueryChange,
  searchRef
}: MediaToolbarProps): ReactElement {
  const options = MEDIA_FILTERS.map((key) => ({
    value: key,
    label: MEDIA_FILTER_LABELS[key],
    count: counts ? counts[key] : undefined
  }))

  return (
    <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
      <SegmentedControl
        label="Filter images"
        value={filter}
        onChange={(value) => {
          const next = toFilter(value)
          if (next) onFilterChange(next)
        }}
        options={options}
        className="max-w-full"
      />

      <div className="flex flex-wrap items-center gap-2 sm:flex-nowrap">
        <div className="min-w-40 flex-1 sm:w-64 sm:flex-none">
          <Input
            ref={searchRef}
            type="text"
            value={query}
            // The round trip is debounced inside the store, so the field itself
            // stays uncontrolled-fast while the network waits.
            onChange={(event) => onQueryChange(event.target.value)}
            aria-label="Search images by title or file name"
            placeholder="Search title or file…"
            leadingIcon={<Search />}
            data-shortcut-search
            autoComplete="off"
          />
        </div>

        {query ? (
          <IconButton label="Clear search" variant="secondary" onClick={() => onQueryChange('')}>
            <X aria-hidden="true" />
          </IconButton>
        ) : null}

        <div className="w-full sm:w-52">
          <Select
            aria-label="Sort images"
            value={sort}
            onChange={(event) => {
              const next = toSort(event.target.value)
              if (next) onSortChange(next)
            }}
            options={SORT_OPTIONS}
          />
        </div>
      </div>
    </div>
  )
}
